import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import './LoginPage.css';

type BrandingState = {
  platform: boolean;
  tenant?: { name: string; code: string } | null;
  branding: {
    theme_color?: string | null;
    login_message?: string | null;
    logo_url?: string | null;
    support_contact?: string | null;
  };
  sso?: {
    enabled?: boolean;
    provider?: string;
    start_path?: string;
    authorize_url?: string;
  } | null;
};

type Mode = 'login' | 'forgot' | 'reset';

function apiBase(): string {
  return typeof window !== 'undefined'
    ? `${window.location.origin}/api/v1`
    : process.env.REACT_APP_API_URL || '/api/v1';
}

function resolveTenantKeyFromLocation(): string | null {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  const fromQuery = params.get('tenant') || params.get('code') || params.get('company');
  if (fromQuery) return fromQuery.trim();

  const host = window.location.hostname || '';
  if (!host || host === 'localhost' || /^\d+\.\d+\.\d+\.\d+$/.test(host)) return null;
  const parts = host.split('.');
  if (parts.length >= 3) {
    const sub = parts[0].toLowerCase();
    if (sub && sub !== 'www' && sub !== 'app' && sub !== 'hub' && sub !== 'api') return sub;
  }
  return null;
}

export default function LoginPage() {
  const { login } = useAuth();
  const [mode, setMode] = useState<Mode>('login');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);
  const [branding, setBranding] = useState<BrandingState | null>(null);
  const tenantKey = useMemo(() => resolveTenantKeyFromLocation(), []);

  useEffect(() => {
    const q = tenantKey ? `?tenant=${encodeURIComponent(tenantKey)}` : '';
    fetch(`${apiBase()}/auth/branding${q}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data) setBranding(data);
      })
      .catch(() => {});
  }, [tenantKey]);

  const theme = branding?.branding?.theme_color || '#4f46e5';
  const title = branding?.tenant?.name ? `${branding.tenant.name} UEM` : 'UEM Console';
  const subtitle =
    branding?.branding?.login_message ||
    (branding?.platform ? 'Sign in to UEM Hub' : 'Client sign-in');
  const logo = branding?.branding?.logo_url || '/Buntu.png';

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setInfo('');
    setLoading(true);
    try {
      await login(username.trim(), password);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleForgot = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setInfo('');
    setLoading(true);
    try {
      const res = await fetch(`${apiBase()}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.detail || data.message || 'Failed to send reset code');
      }
      setInfo(data.message || 'If an account exists for that email, a one-time code has been sent.');
      if (data.debug_otp) {
        setInfo((prev: string) => `${prev} (Dev OTP: ${data.debug_otp})`);
        setOtp(String(data.debug_otp));
      }
      setMode('reset');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send reset code');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setInfo('');
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${apiBase()}/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim(),
          otp: otp.trim(),
          new_password: newPassword,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const detail = data.detail;
        throw new Error(typeof detail === 'string' ? detail : data.message || 'Reset failed');
      }
      setInfo(data.message || 'Password updated. You can sign in now.');
      if (data.username) setUsername(data.username);
      setPassword('');
      setOtp('');
      setNewPassword('');
      setConfirmPassword('');
      setMode('login');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Reset failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="login-page"
      style={{
        background: `linear-gradient(145deg, #0f172a 0%, ${theme}55 55%, #0f172a 100%)`,
      }}
    >
      <div className="login-card" style={{ borderTop: `4px solid ${theme}` }}>
        <div className="login-header">
          <img
            src={logo}
            alt={branding?.tenant?.name || 'UEM'}
            className="login-logo"
            onError={(e) => {
              (e.target as HTMLImageElement).src = '/Buntu.png';
            }}
          />
          <h1 className="login-title">{title}</h1>
          <p className="login-subtitle">
            {mode === 'login'
              ? subtitle
              : mode === 'forgot'
                ? 'Reset your password with an email code'
                : 'Enter the OTP sent to your email'}
          </p>
          {branding?.tenant?.code && mode === 'login' && (
            <p className="login-tenant-code">{branding.tenant.code}</p>
          )}
        </div>

        {mode === 'login' && (
          <form className="login-form" onSubmit={handleLogin}>
            {error && <div className="login-error">{error}</div>}
            {info && <div className="login-info">{info}</div>}
            <div className="login-field">
              <label htmlFor="username">Email</label>
              <input
                id="username"
                type="email"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="you@company.com"
                autoComplete="email"
                autoFocus
                required
              />
            </div>
            <div className="login-field">
              <label htmlFor="password">Password</label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                autoComplete="current-password"
                required
              />
            </div>
            <button type="submit" className="login-btn" disabled={loading} style={{ background: theme }}>
              {loading ? 'Signing in...' : 'Sign in'}
            </button>
            <button
              type="button"
              className="login-link-btn"
              onClick={() => {
                setMode('forgot');
                setError('');
                setInfo('');
                if (username.includes('@')) setEmail(username.trim());
              }}
            >
              Forgot password?
            </button>
            {branding?.sso?.enabled && branding?.sso?.start_path && (
              <a
                className="login-btn"
                href={`${typeof window !== 'undefined' ? window.location.origin : ''}${branding.sso.start_path}`}
                style={{
                  background: 'transparent',
                  border: `1px solid ${theme}`,
                  color: theme,
                  marginTop: 8,
                  textAlign: 'center',
                  textDecoration: 'none',
                  display: 'block',
                }}
              >
                Sign in with SSO
              </a>
            )}
          </form>
        )}

        {mode === 'forgot' && (
          <form className="login-form" onSubmit={handleForgot}>
            {error && <div className="login-error">{error}</div>}
            {info && <div className="login-info">{info}</div>}
            <div className="login-field">
              <label htmlFor="email">Account email</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                autoComplete="email"
                autoFocus
                required
              />
            </div>
            <button type="submit" className="login-btn" disabled={loading} style={{ background: theme }}>
              {loading ? 'Sending…' : 'Send OTP'}
            </button>
            <button
              type="button"
              className="login-link-btn"
              onClick={() => {
                setMode('login');
                setError('');
                setInfo('');
              }}
            >
              Back to sign in
            </button>
          </form>
        )}

        {mode === 'reset' && (
          <form className="login-form" onSubmit={handleReset}>
            {error && <div className="login-error">{error}</div>}
            {info && <div className="login-info">{info}</div>}
            <div className="login-field">
              <label htmlFor="email2">Email</label>
              <input id="email2" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
            </div>
            <div className="login-field">
              <label htmlFor="otp">One-time code (OTP)</label>
              <input
                id="otp"
                type="text"
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="6-digit code"
                autoComplete="one-time-code"
                autoFocus
                required
              />
            </div>
            <div className="login-field">
              <label htmlFor="newPassword">New password</label>
              <input
                id="newPassword"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="At least 8 characters"
                autoComplete="new-password"
                required
                minLength={8}
              />
            </div>
            <div className="login-field">
              <label htmlFor="confirmPassword">Confirm password</label>
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Repeat new password"
                autoComplete="new-password"
                required
                minLength={8}
              />
            </div>
            <button type="submit" className="login-btn" disabled={loading} style={{ background: theme }}>
              {loading ? 'Updating…' : 'Reset password'}
            </button>
            <button
              type="button"
              className="login-link-btn"
              onClick={() => {
                setMode('forgot');
                setError('');
                setInfo('');
              }}
            >
              Resend code
            </button>
            <button
              type="button"
              className="login-link-btn"
              onClick={() => {
                setMode('login');
                setError('');
                setInfo('');
              }}
            >
              Back to sign in
            </button>
          </form>
        )}

        {branding?.branding?.support_contact && (
          <p className="login-support">Support: {branding.branding.support_contact}</p>
        )}
      </div>
    </div>
  );
}
